import React, { FunctionComponent, MutableRefObject, useEffect, useMemo, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Popover } from 'react-native-popper'

import { EventContentArg, formatDate, formatRange } from '@fullcalendar/core'
import { addMinutes, differenceInMinutes, isPast, parseISO } from 'date-fns'
import { isNil } from 'lodash-es'
import { CSSObject } from 'styled-components'
import styled, { useTheme } from 'styled-components/native'

import { CLOSE_SESSION_TOOLTIP_TEXT } from '@lyrahealth-inc/shared-app-logic/src/features/calendar/provider/messages'
import { CLOSE_SESSION_TYPES } from '@lyrahealth-inc/shared-app-logic/src/features/calendar/provider/types'
import {
  getAdjustedEvent,
  isBookableEvent,
  isGenericEvent,
  OffsetDirection,
} from '@lyrahealth-inc/shared-app-logic/src/features/calendar/provider/utils'
import { toLocalISOString } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/timeZoneUtils'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import {
  getProgramDisplayLabel,
  getProgramNameFromId,
} from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'
import { SessionTypeText } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/messages'
import {
  AppointmentBookedDetails,
  BendAppointmentType,
  EventExtendedProps,
  getNpSessionLabel,
  getNpSessionTypeFromBookingType,
  isBendEvent,
} from '@lyrahealth-inc/shared-app-logic/src/models/calendar/CalendarV2'
import { MEETING_FORMATS } from '@lyrahealth-inc/shared-app-logic/src/models/session/Session'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { AlertIconStroke } from '../../atoms/icons/AlertIconStroke'
import { AttentionIcon } from '../../atoms/icons/AttentionIcon'
import { CheckMarkInCircleIcon } from '../../atoms/icons/CheckMarkInCircleIcon'
import { ClipboardIcon } from '../../atoms/icons/ClipboardIcon'
import { ClipboardWithArrowIcon } from '../../atoms/icons/ClipboardWithArrowIcon'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { Title } from '../../atoms/title/Title'
import { Tooltip } from '../../atoms/tooltip/Tooltip'
import { useGetCloseSessionType } from '../../hooks/useGetCloseSessionType'
import { BodyTextSize, TitleSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { ProviderCalendarPopover } from '../providerCalendarPopover/ProviderCalendarPopover'

export type ProviderCalendarEventProps = {
  args: EventContentArg
  startingRange: MutableRefObject<{ startStr: string; endStr: string } | null> | null
  timeZone?: string
  getAppointmentBookedDetails?: (appointmentId: number) => Promise<AppointmentBookedDetails>
  onClientProfilePressed?: (clientId: string) => void
  onDelete?: (event: EventContentArg) => void
  onEdit?: (event: EventContentArg) => void
  showPopover?: boolean
  onOpenInGoogleCalendar?: (externalId: string, calendarConfigurationId: string) => void
  onOpenVideoConferencing?: (externalId: string, calendarConfigurationId: string) => void
  isTemplate?: boolean
  onCancelPressed?: (appointmentId: number) => void
  onCancelMosAppointmentPressed?: (appointmentId: number) => void
  onOpenPopover?: ({ appointmentId, clientId }: { appointmentId: number; clientId: string }) => void
  onReschedule?: (appointmentId: number) => void
  onCloseSession?: (appointmentId: number, clientId: string | null) => void
  onCompleteNote?: (appointmentId: number, clientId: string) => void
  onViewCompletedNote?: (appointmentId: number) => void
  hideCalendarEventDetails?: boolean
  hybridBookingMode?: boolean
  displayAppointmentBookedDetails?: boolean
  showRiskAlerts?: boolean
  engageDAProvider?: boolean
  supportsOnOffPlatformVideo?: boolean
  sessionNeedsNote?: boolean
  onScheduleSession?: () => void
  showModalitySelection?: boolean
  // When true, the video modality is labeled "Virtual" instead of "Video".
  renderVideoAsVirtual?: boolean
  showLateCancellations?: boolean
  clientVoicemailConsent?: boolean
  guardianVoicemailConsent?: boolean
  guardianPhone?: string
  clientPronouns?: string[]
  isClientDataLoading?: boolean
  flexibleAvailabilityWindows?: boolean
  handleOpenEditModality?: (arg: EventContentArg, reopenCallback?: () => void) => void
  enableSessionModalityEditability?: boolean
  conflictTooltipText?: string
}

type EventStyles = {
  container: {
    backgroundColor: string
    borderColor: string
  }
  textColor?: string
}

const SessionType = styled(BodyText)({
  textTransform: 'capitalize',
})

const EventContainer = styled(PressableOpacity)<{
  offset?: OffsetDirection
  hasConflict?: boolean
  styles?: CSSObject
  outsideBookingHours?: boolean
}>(({ theme, offset = OffsetDirection.NONE, hasConflict = false, styles, outsideBookingHours = false }) => ({
  flexDirection: 'column',
  height:
    offset !== OffsetDirection.NONE
      ? `calc(100% + ${offset === OffsetDirection.BEFORE_AND_AFTER ? '82px' : '41px'})`
      : '100%',
  marginTop: offset === OffsetDirection.BEFORE_AND_AFTER ? '-41px' : '0px',
  borderWidth: '1px',
  padding: theme.spacing['4px'],
  borderRadius: '4px',
  gap: theme.spacing['4px'],
  width: '100%',
  ...styles,
  ...((hasConflict || outsideBookingHours) && {
    borderLeftWidth: '2px',
    borderLeftColor: theme.colors.borderWarning,
  }),
}))

const EventRow = styled.View({
  flexDirection: 'row',
  justifyContent: 'space-between',
})

const SelectedAmountBadge = styled(Title)(({ theme }) => ({
  position: 'absolute',
  flexDirection: 'row',
  bottom: -11,
  right: 11,
  backgroundColor: theme.colors.backgroundHover,
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
  height: '22px',
  width: '22px',
  borderRadius: '11px',
  color: theme.colors.textInverse,
}))

const RightIconContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  gap: theme.spacing['4px'],
}))

export const ProviderCalendarEvent: FunctionComponent<ProviderCalendarEventProps> = ({
  args,
  startingRange,
  timeZone = 'America/Los_Angeles',
  showPopover = true,
  getAppointmentBookedDetails,
  onClientProfilePressed,
  onDelete = () => {},
  onEdit = () => {},
  onOpenInGoogleCalendar,
  onOpenVideoConferencing,
  isTemplate = false,
  onCancelPressed,
  onCancelMosAppointmentPressed,
  onOpenPopover,
  onReschedule,
  onCloseSession,
  onCompleteNote,
  onViewCompletedNote,
  hideCalendarEventDetails = false,
  hybridBookingMode = false,
  displayAppointmentBookedDetails = false,
  showRiskAlerts,
  engageDAProvider = false,
  supportsOnOffPlatformVideo = false,
  sessionNeedsNote = false,
  onScheduleSession,
  showModalitySelection = false,
  renderVideoAsVirtual = false,
  showLateCancellations = false,
  clientVoicemailConsent,
  guardianVoicemailConsent,
  guardianPhone,
  clientPronouns,
  isClientDataLoading,
  flexibleAvailabilityWindows = false,
  handleOpenEditModality,
  enableSessionModalityEditability,
  conflictTooltipText,
}) => {
  const intl = useIntl()
  const theme = useTheme() as ThemeType
  const pressableRef = useRef(null)
  const [isOpen, setIsOpen] = useState(false)
  const [isOverlapped, setIsOverlapped] = useState(false)

  const eventProperties = args.event.extendedProps as EventExtendedProps
  const shouldShowAppointmentAddress =
    showModalitySelection &&
    eventProperties?.lyraEventType === 'session' &&
    !isBendEvent(eventProperties) &&
    eventProperties?.appointmentMeetingFormat === MEETING_FORMATS.IN_PERSON &&
    eventProperties?.appointmentAddress

  const isLegacyDAAppointment =
    eventProperties.lyraEventType === 'session' && !isBendEvent(eventProperties) && isNil(eventProperties.lyraProgramId)

  const { isCoreCareNPIntakeSessionEnabled } = useFlags()

  const eventTitle = useMemo(() => {
    if (eventProperties.lyraEventType === 'bookable' || eventProperties.lyraEventType === 'bookable_recurring') {
      let title = args.event.title
      const npSessionType = getNpSessionTypeFromBookingType(eventProperties.booking_type)
      if (npSessionType) {
        title += ` - ${getNpSessionLabel(npSessionType)}`
      } else if (isCoreCareNPIntakeSessionEnabled) {
        title += ' - Core Care'
      }
      if (eventProperties.lyraEventType === 'bookable' && !isTemplate) {
        title += ' (one-time)'
      }
      return title
    }

    if (
      eventProperties.lyraEventType !== 'session' ||
      isBendEvent(eventProperties) ||
      !eventProperties.clientFirstName ||
      !eventProperties.clientLastName ||
      eventProperties.appointmentSessionNumber == null ||
      (!eventProperties.lyraProgramId && !hybridBookingMode) ||
      !eventProperties.appointmentClass
    ) {
      return args.event.title
    }

    const programName = getProgramNameFromId(eventProperties.lyraProgramId)
    const programNameLabel = programName ? getProgramDisplayLabel(intl, programName, 'short') : ''
    if (showLateCancellations && (eventProperties.isCoveredLateCancel || eventProperties.isClientOwedLateCancel)) {
      return (
        <FormattedMessage
          defaultMessage='{firstName} {lastInitial}. • Cancelled {programNameLabel}'
          description='Event title for late cancelled lyra appointment'
          values={{
            firstName: eventProperties.clientFirstName,
            lastInitial: eventProperties.clientLastName.charAt(0),
            programNameLabel: programNameLabel === '' ? '' : `• ${programNameLabel}`,
          }}
        />
      )
    }
    if (eventProperties.isNoShow) {
      return (
        <FormattedMessage
          defaultMessage='{firstName} {lastInitial}. • No Show {programNameLabel}'
          description='Event title for lyra appointment'
          values={{
            firstName: eventProperties.clientFirstName,
            lastInitial: eventProperties.clientLastName.charAt(0),
            programNameLabel: programNameLabel === '' ? '' : `• ${programNameLabel}`,
          }}
        />
      )
    }

    if (
      eventProperties.appointmentClass === 'initial' &&
      !(isLegacyDAAppointment && eventProperties.providerScheduled)
    ) {
      return (
        <FormattedMessage
          defaultMessage='{firstName} {lastInitial}. • Intake {programNameLabel}'
          description='Event title for lyra appointment'
          values={{
            firstName: eventProperties.clientFirstName,
            lastInitial: eventProperties.clientLastName.charAt(0),
            programNameLabel: hideCalendarEventDetails || programNameLabel === '' ? '' : `• ${programNameLabel}`,
          }}
        />
      )
    }
    return (
      <FormattedMessage
        defaultMessage='{firstName} {lastInitial}. {programNameLabel}'
        description='Event title for lyra appointment'
        values={{
          firstName: eventProperties.clientFirstName,
          lastInitial: eventProperties.clientLastName.charAt(0),
          programNameLabel: programNameLabel === '' ? '' : `• ${programNameLabel}`,
        }}
      />
    )
  }, [
    args.event.title,
    eventProperties,
    isCoreCareNPIntakeSessionEnabled,
    isTemplate,
    hideCalendarEventDetails,
    hybridBookingMode,
    intl,
    showLateCancellations,
    isLegacyDAAppointment,
  ])

  const isOOO = isGenericEvent(eventProperties) && eventProperties.isOOOEvent
  const outsideBookingHours = eventProperties.outsideBookingHours ?? false
  useEffect(() => {
    if (!pressableRef.current) {
      return
    }

    const buttonEl = pressableRef.current as HTMLElement

    const parentHarness = buttonEl.closest('.fc-timegrid-event-harness')
    if (parentHarness && parentHarness instanceof HTMLElement) {
      setIsOverlapped(parseInt(parentHarness.style.zIndex) > 1)
    }
  }, [pressableRef])
  const isBookableSlot = isBookableEvent(eventProperties)
  const durationMinutes = differenceInMinutes(parseISO(args.event.endStr), parseISO(args.event.startStr))

  const startDate = toLocalISOString(args.event.startStr, timeZone)
  const endDate = toLocalISOString(args.event.endStr, timeZone)
  const useOneLine = durationMinutes < 30
  const displayStartTimeOnly = isBookableSlot || useOneLine || (isOverlapped && durationMinutes < 60)
  const displayTimeText = !isOverlapped || !useOneLine
  const sessionStartDate = new Date(startDate)
  const isInPast = isPast(sessionStartDate)

  const eventStyles = useMemo<EventStyles>(() => {
    const eventColors = theme.colors.components.providerCalendar.event
    if (args.isMirror && !args.isDragging) {
      return {
        container: {
          backgroundColor: eventColors.mirror.background,
          borderColor: eventColors.mirror.border,
        },
      }
    }

    if (isBendEvent(eventProperties)) {
      const IS_EVALUATION_TYPE: Record<BendAppointmentType, boolean> = {
        bcm_evaluation: true,
        coach_evaluation: true,
        therapist_evaluation: true,
        prescriber_evaluation: true,
        psychometrist_evaluation: true,
        bcm_session: false,
        coach_session: false,
        therapist_session: false,
        prescriber_session: false,
        nurse_session: false,
        psychometrist_session: false,
        neuropsychologist_session: false,
      }
      const isEvaluation = IS_EVALUATION_TYPE[eventProperties.bendAppointmentType]
      const evaluationColor = isInPast ? eventColors.bendInitialSessionPast : eventColors.bendInitialSession
      const sessionColor = isInPast ? eventColors.bendRecurringSessionPast : eventColors.bendRecurringSession
      const colorKey = isEvaluation ? evaluationColor : sessionColor
      return {
        container: {
          backgroundColor: colorKey.background,
          borderColor: colorKey.border,
        },
        textColor: colorKey.text,
      }
    }

    if (isGenericEvent(eventProperties)) {
      if (isOOO) {
        return {
          container: {
            backgroundColor: eventColors.ooo.background,
            borderColor: eventColors.ooo.border,
          },
        }
      }
      return {
        container: {
          backgroundColor: eventColors.default.background,
          borderColor: eventColors.default.border,
        },
      }
    }

    if (isBookableSlot) {
      const isBackgroundEvent = args.event.display === 'background'
      if (eventProperties.hovered === true) {
        return {
          textColor: theme.colors.textSecondary,
          container: {
            backgroundColor: eventColors.bookableHover.background,
            borderColor: isBackgroundEvent ? 'transparent' : eventColors.bookableHover.border,
          },
        }
      }
      return {
        textColor: theme.colors.textSecondary,
        container: {
          backgroundColor: eventColors.bookable.background,
          borderColor: isBackgroundEvent ? 'transparent' : eventColors.bookable.border,
        },
      }
    }

    if (eventProperties.lyraEventType === 'session' && eventProperties.appointmentClass === 'initial') {
      if (isInPast) {
        return {
          container: {
            backgroundColor: eventColors.initialSessionPast.background,
            borderColor: eventColors.initialSessionPast.border,
          },
          textColor: eventColors.initialSessionPast.text,
        }
      }
      return {
        container: {
          backgroundColor: eventColors.initialSession.background,
          borderColor: eventColors.initialSession.border,
        },
        textColor: eventColors.initialSession.text,
      }
    }
    if (isInPast) {
      return {
        container: {
          backgroundColor: eventColors.recurringSessionPast.background,
          borderColor: eventColors.recurringSessionPast.border,
        },
        textColor: eventColors.recurringSessionPast.text,
      }
    }
    return {
      container: {
        backgroundColor: eventColors.recurringSession.background,
        borderColor: eventColors.recurringSession.border,
      },
      textColor: eventColors.recurringSession.text,
    }
  }, [
    args.event.display,
    args.isDragging,
    args.isMirror,
    eventProperties,
    isBookableSlot,
    isInPast,
    isOOO,
    theme.colors.components.providerCalendar.event,
    theme.colors.textSecondary,
  ])

  // These variables and hook must be before the early return to satisfy React's rules of hooks
  const isLyraSession = eventProperties.lyraEventType === 'session' && !isBendEvent(eventProperties)
  const appointmentStatus = isLyraSession ? eventProperties.appointmentStatus : undefined
  const isChargeReversed = isLyraSession ? (eventProperties.isChargeReversed ?? false) : false
  const isCoveredLateCancel = isLyraSession ? (eventProperties.isCoveredLateCancel ?? false) : false
  const isClientOwedLateCancel = isLyraSession ? (eventProperties.isClientOwedLateCancel ?? false) : false

  const closeSessionType = useGetCloseSessionType({
    appointmentStatus,
    engageDAProvider,
    isInPast,
    lyraEventType: eventProperties.lyraEventType,
    onCloseSession,
    onCompleteNote,
    onViewCompletedNote,
    sessionNeedsNote,
    isLegacyDAAppointment,
    isChargeReversed,
    isCoveredLateCancel,
    isClientOwedLateCancel,
    showLateCancellations,
  })

  if (args.isMirror && !args.isDragging && startingRange) {
    const differenceHours = durationMinutes / 60
    // For flexible mode, skip the 30-minute rounding; for non-flexible mode, keep old behavior
    if (!flexibleAvailabilityWindows && differenceHours === 0.5) {
      startingRange.current = {
        startStr: args.event.startStr,
        endStr: addMinutes(parseISO(args.event.endStr), 30).toISOString(),
      }
    }
    const adjustedEvent = getAdjustedEvent(args.event, startingRange?.current ?? null, {
      skipRounding: flexibleAvailabilityWindows,
    })
    return (
      <EventContainer offset={adjustedEvent.offset} style={eventStyles.container}>
        <BodyText
          style={{ lineHeight: 14 }}
          color={theme.colors.textSecondary}
          size={BodyTextSize.CAPTION}
          text={formatRange(adjustedEvent.start.slice(0, -1), adjustedEvent.end.slice(0, -1), {
            hour: 'numeric',
            minute: 'numeric',
          })}
        />
        <SelectedAmountBadge
          color={theme.colors.textSecondary}
          size={TitleSize.XSMALL}
          text={Math.ceil(differenceInMinutes(parseISO(adjustedEvent.end), parseISO(adjustedEvent.start)) / 60)}
        />
      </EventContainer>
    )
  }

  const timeText = displayTimeText ? (
    <BodyText
      style={{ lineHeight: 12, flexShrink: 0 }}
      color={eventStyles.textColor ?? theme.colors.textSecondary}
      size={BodyTextSize.CAPTION}
      numberOfLines={useOneLine ? 1 : undefined}
      text={
        displayStartTimeOnly
          ? formatDate(args.event.startStr, { hour: 'numeric', minute: 'numeric', timeZone })
          : formatRange(args.event.startStr, args.event.endStr, {
              hour: 'numeric',
              minute: 'numeric',
              timeZone,
            })
      }
      testID={tID('ProviderCalendarEvent-timeText')}
    />
  ) : null

  const sessionType =
    eventProperties?.lyraEventType === 'session' &&
    !isBendEvent(eventProperties) &&
    eventProperties?.appointmentMeetingFormat &&
    eventProperties?.appointmentMeetingFormat in SessionTypeText ? (
      <SessionType
        style={{ lineHeight: 12, flexShrink: 0 }}
        color={eventStyles.textColor ?? theme.colors.textSecondary}
        size={BodyTextSize.CAPTION}
        numberOfLines={useOneLine || shouldShowAppointmentAddress ? 1 : undefined}
        text={
          shouldShowAppointmentAddress
            ? eventProperties?.appointmentAddress
            : renderVideoAsVirtual && eventProperties?.appointmentMeetingFormat === MEETING_FORMATS.VIDEO
              ? intl.formatMessage({
                  defaultMessage: 'Virtual',
                  description:
                    'Virtual (video) appointment modality label shown to Military OneSource (MOS) approved providers',
                })
              : SessionTypeText[eventProperties?.appointmentMeetingFormat]
        }
        testID={tID('ProviderCalendarEvent-sessionType')}
      />
    ) : null

  const hasConflict = eventProperties.hasConflict ?? false

  const closeSessionTooltipText =
    closeSessionType && closeSessionType in CLOSE_SESSION_TOOLTIP_TEXT
      ? CLOSE_SESSION_TOOLTIP_TEXT[closeSessionType as keyof typeof CLOSE_SESSION_TOOLTIP_TEXT]
      : undefined

  const isLateCancel = showLateCancellations && (isCoveredLateCancel || isClientOwedLateCancel)

  // bookable modality labels (Video / In-person) gated by showModalitySelection
  const shouldShowModalityLabels =
    showModalitySelection &&
    (eventProperties.lyraEventType === 'bookable' || eventProperties.lyraEventType === 'bookable_recurring')

  const videoAvailable = !!eventProperties.modality_availability?.video?.available
  const inPersonAvailable = !!eventProperties.modality_availability?.in_person?.available

  return (
    <>
      <EventContainer
        testID={tID(`ProviderCalendarEvent-container${isLyraSession ? '-' + eventProperties.lyraAppointmentId : ''}`)}
        styles={eventStyles.container}
        pressableRef={pressableRef}
        onPress={() => setIsOpen(!isOpen)}
        hasConflict={hasConflict}
        outsideBookingHours={outsideBookingHours}
      >
        <EventRow>
          <BodyText
            style={{ lineHeight: 14, ...(isLateCancel && { textDecorationLine: 'line-through' }) }}
            numberOfLines={1}
            color={eventStyles.textColor ?? theme.colors.textPrimary}
            size={isBookableSlot ? BodyTextSize.CAPTION : BodyTextSize.BADGE}
            text={eventTitle}
            testID={tID('ProviderCalendarEvent-title')}
          />
          {useOneLine && timeText}
          <RightIconContainer>
            {closeSessionTooltipText && (
              <Tooltip
                content={<FormattedMessage {...closeSessionTooltipText} />}
                testID={tID('closeSessionIcon')}
                hoverEnabled
                placement='top'
              >
                {closeSessionType === CLOSE_SESSION_TYPES.INVOICE_NEEDED_BY_ADMIN ||
                closeSessionType === CLOSE_SESSION_TYPES.NOTE_NEEDED_BY_PROVIDER ? (
                  <ClipboardWithArrowIcon size={16} color={eventStyles.textColor ?? theme.colors.textPrimary} />
                ) : (
                  <ClipboardIcon size={16} color={eventStyles.textColor ?? theme.colors.textPrimary} />
                )}
              </Tooltip>
            )}
            {isOOO && (
              <Tooltip content='Verified OOO' hoverEnabled>
                <CheckMarkInCircleIcon size={16} fillColor={theme.colors.components.providerCalendar.badge.ooo.text} />
              </Tooltip>
            )}
            {hasConflict && (
              <Tooltip
                content={
                  hybridBookingMode ? 'Conflict: This time will not be shown to clients for booking.' : 'Conflict'
                }
                hoverEnabled
              >
                <AlertIconStroke size={16} fillColor={theme.colors.iconWarning} />
              </Tooltip>
            )}
            {outsideBookingHours && (
              <Tooltip content='Outside booking hours' hoverEnabled>
                <AttentionIcon size={18} fillColor={theme.colors.iconWarning} />
              </Tooltip>
            )}
          </RightIconContainer>
        </EventRow>
        {!useOneLine && timeText}
        {shouldShowModalityLabels && (
          <BodyText
            style={{ lineHeight: 12, flexShrink: 1 }}
            color={eventStyles.textColor ?? theme.colors.textSecondary}
            size={BodyTextSize.CAPTION}
            text={
              videoAvailable || inPersonAvailable ? (
                <>
                  {videoAvailable && (
                    <FormattedMessage defaultMessage='Video' description='Video modality label on bookable slot' />
                  )}
                  {videoAvailable && inPersonAvailable ? ' · ' : ''}
                  {inPersonAvailable && (
                    <FormattedMessage
                      defaultMessage='In-person'
                      description='In-person modality label on bookable slot'
                    />
                  )}
                </>
              ) : (
                <FormattedMessage
                  defaultMessage='—'
                  description='Em dash placeholder for when no appointment type is available'
                />
              )
            }
          />
        )}
        {sessionType}
      </EventContainer>
      {showPopover && (
        <Popover
          isOpen={isOpen}
          onOpenChange={setIsOpen}
          trigger={pressableRef}
          placement='right top'
          animated={!window.Cypress}
        >
          <Popover.Content>
            <ProviderCalendarPopover
              eventContentArg={args}
              title={args.event.title}
              startDate={startDate}
              endDate={endDate}
              eventStartStr={args.event.startStr}
              eventEndStr={args.event.endStr}
              extendedProps={eventProperties}
              timeZone={timeZone}
              onClose={() => setIsOpen(false)}
              onClientProfilePressed={onClientProfilePressed}
              onDelete={() => {
                setIsOpen(false)
                onDelete(args)
              }}
              onEdit={() => {
                setIsOpen(false)
                onEdit(args)
              }}
              onCancel={onCancelPressed}
              onCancelMosAppointment={
                onCancelMosAppointmentPressed
                  ? (appointmentId) => {
                      setIsOpen(false)
                      onCancelMosAppointmentPressed(appointmentId)
                    }
                  : undefined
              }
              onOpenPopover={onOpenPopover}
              onReschedule={onReschedule}
              onCloseSession={onCloseSession}
              onCompleteNote={onCompleteNote}
              onViewCompletedNote={onViewCompletedNote}
              closeSessionType={closeSessionType}
              onOpenInGoogleCalendar={onOpenInGoogleCalendar}
              onOpenVideoConferencing={onOpenVideoConferencing}
              isTemplate={isTemplate}
              hideCalendarEventDetails={hideCalendarEventDetails}
              hybridBookingMode={hybridBookingMode}
              displayAppointmentBookedDetails={displayAppointmentBookedDetails}
              showRiskAlerts={showRiskAlerts}
              getAppointmentBookedDetails={getAppointmentBookedDetails}
              onScheduleSession={onScheduleSession}
              showModalitySelection={showModalitySelection}
              isLegacyDAAppointment={isLegacyDAAppointment}
              engageDAProvider={engageDAProvider}
              showLateCancellations={showLateCancellations}
              clientVoicemailConsent={clientVoicemailConsent}
              guardianVoicemailConsent={guardianVoicemailConsent}
              guardianPhone={guardianPhone}
              clientPronouns={clientPronouns}
              isClientDataLoading={isClientDataLoading}
              enableSessionModalityEditability={enableSessionModalityEditability}
              onOpenEditModality={() => {
                handleOpenEditModality?.(args, () => setIsOpen(true))
                setIsOpen(false)
              }}
              conflictTooltipText={conflictTooltipText}
              supportsOnOffPlatformVideo={supportsOnOffPlatformVideo}
            />
          </Popover.Content>
        </Popover>
      )}
    </>
  )
}
